import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Shield, ExternalLink, ChevronRight, Twitter, Instagram, MessageCircle, Send, BarChart3, DollarSign, Brain, Gamepad2, ShieldAlert, LineChart, Target, Rocket, Heart, Mail, TrendingUp, Calculator } from 'lucide-react';

const FEATURED_TOOLS = [
  { Icon: BarChart3, iconColor: '#10b981', badge: 'Most Used', badgeColor: '#4d7cfe', name: 'RTP Comparison Engine', desc: 'Compare 5,000+ games by Return-to-Player rates', stat: '15,300 uses this month', cta: 'Try It Free', path: '/play-smart/rtp-comparison-engine' },
  { Icon: DollarSign, iconColor: '#4d7cfe', badge: "Editor's Pick", badgeColor: '#8b5cf6', name: 'Gambling Budget Calculator', desc: 'Calculate your safe monthly gambling budget', stat: '12,450 uses this month', cta: 'Calculate Now', path: '/play-smart/gambling-budget-calculator' },
  { Icon: Brain, iconColor: '#4d7cfe', badge: 'Trending', badgeColor: '#10b981', name: 'Gambler Personality Quiz', desc: 'What type of gambler are you? Take the quiz', stat: '8,920 completions', cta: 'Take Quiz', path: '/viral-tools/gambler-personality-quiz' },
  { Icon: Gamepad2, iconColor: '#8b5cf6', name: 'Cost2Play Calculator', desc: 'See exactly how much each game costs over time', stat: '7,680 uses this month', cta: 'Calculate Cost', path: '/play-smart/cost2play-calculator' },
  { Icon: ShieldAlert, iconColor: '#ef4444', badge: 'New', badgeColor: '#f59e0b', name: 'Loss-Chase Detector', desc: 'AI-powered tool detects and prevents loss-chasing', stat: '3,240 interventions', cta: 'Activate Protection', path: '/emotional-wellness/loss-chase-detector' },
  { Icon: LineChart, iconColor: '#4d7cfe', name: 'Bankroll Simulator', desc: 'Monte Carlo simulation of your betting strategy', stat: '5,130 simulations run', cta: 'Run Simulation', path: '/play-smart/bankroll-simulator' },
];

const ALL_TOOLS = {
  'Play Smart': { color: '#4d7cfe', tools: [
    { name: 'Budget Calculator', path: '/play-smart/gambling-budget-calculator' },
    { name: 'Cost2Play Calculator', path: '/play-smart/cost2play-calculator' },
    { name: 'RTP Comparison Engine', path: '/play-smart/rtp-comparison-engine' },
    { name: 'Bankroll Simulator', path: '/play-smart/bankroll-simulator' },
    { name: 'Bonus Decoder', path: '/play-smart/bonus-terms-decoder' },
    { name: 'Pre-Deposit Checklist', path: '/play-smart/pre-deposit-checklist' },
    { name: 'Withdrawal Predictor', path: '/play-smart/withdrawal-predictor' },
  ]},
  'Viral Tools': { color: '#8b5cf6', tools: [
    { name: 'Personality Quiz', path: '/viral-tools/gambler-personality-quiz' },
    { name: 'Win Predictor', path: '/viral-tools/win-prediction-simulator' },
    { name: 'Friend Challenge', path: '/viral-tools/friend-challenge' },
    { name: 'Spin to Win', path: '/viral-tools/spin-to-win' },
    { name: 'Volatility Matcher', path: '/viral-tools/slot-volatility-matcher' },
    { name: 'Betting Streak Tracker', path: '/viral-tools/betting-streak-tracker' },
  ]},
  'Emotional Wellness': { color: '#10b981', tools: [
    { name: 'Mood Tracker', path: '/emotional-wellness/mood-tracker' },
    { name: 'Loss-Chase Detector', path: '/emotional-wellness/loss-chase-detector' },
    { name: 'Win Celebration', path: '/emotional-wellness/win-celebration' },
    { name: 'Session Timer', path: '/emotional-wellness/session-timer' },
    { name: 'Alternative Activities', path: '/emotional-wellness/alternatives' },
    { name: 'Regret Anticipation', path: '/emotional-wellness/regret-anticipation' },
  ]},
};

// Floating icon component
function FloatingIcon({ Icon, color, style }) {
  return (
    <span className="absolute opacity-[0.08] pointer-events-none animate-float select-none" style={style}>
      <Icon className="h-10 w-10" style={{ color }} />
    </span>
  );
}

export default function LandingPage() {
  const [email, setEmail] = useState('');
  const navigate = useNavigate();

  const handleGetAccess = (e) => {
    e.preventDefault();
    navigate('/signup', { state: { email } });
  };

  return (
    <div className="min-h-screen bg-[#1a1f2e]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-[#1a1f2e]/95 backdrop-blur-lg border-b border-white/5 z-50">
        <div className="max-w-[1280px] mx-auto h-full flex items-center justify-between px-4 lg:px-8">
          <span className="text-xl font-bold text-white tracking-tight">Casino<span className="text-[#4d7cfe]">IQ</span></span>
          <div className="flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 text-sm font-medium text-[#a0a8b8] hover:text-white transition-colors" data-testid="landing-login-btn">Log In</Link>
            <Link to="/signup" className="px-5 py-2 rounded-lg bg-[#4d7cfe] text-white text-sm font-semibold hover:brightness-110 transition-all" data-testid="landing-signup-btn">Get Started</Link>
          </div>
        </div>
      </header>

      {/* S1: Hero */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 px-4 overflow-hidden" data-testid="hero-section">
        <FloatingIcon Icon={DollarSign} color="#4d7cfe" style={{ top: '18%', left: '8%', animationDelay: '0s' }} />
        <FloatingIcon Icon={Gamepad2} color="#8b5cf6" style={{ top: '30%', right: '10%', animationDelay: '1.2s' }} />
        <FloatingIcon Icon={BarChart3} color="#10b981" style={{ bottom: '25%', left: '15%', animationDelay: '2.4s' }} />
        <FloatingIcon Icon={Calculator} color="#f59e0b" style={{ top: '20%', right: '25%', animationDelay: '0.8s' }} />
        <FloatingIcon Icon={TrendingUp} color="#4d7cfe" style={{ bottom: '30%', right: '8%', animationDelay: '1.8s' }} />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-bold text-white leading-[1.1] animate-fade-in" style={{ letterSpacing: '-0.02em' }} data-testid="hero-headline">
            Play Smarter. Win Better.<br className="hidden sm:block" /> Know More.
          </h1>
          <p className="text-base lg:text-xl text-[#a0a8b8] mt-6 max-w-2xl mx-auto leading-relaxed animate-fade-in stagger-1" data-testid="hero-subheading">
            Access 19 premium casino intelligence tools. Make data-driven decisions.<br className="hidden sm:block" />
            Take control of your game.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10 animate-fade-in stagger-2">
            <Link to="/signup" className="w-full sm:w-auto px-8 py-3.5 rounded-lg bg-[#4d7cfe] text-white text-lg font-semibold hover:brightness-110 hover:-translate-y-0.5 transition-all shadow-lg shadow-[#4d7cfe]/20" data-testid="hero-cta-primary">
              Get Free Access
            </Link>
            <Link to="/tools" className="w-full sm:w-auto px-8 py-3.5 rounded-lg border border-white/20 text-white text-lg font-medium hover:border-[#4d7cfe] transition-colors text-center" data-testid="hero-cta-secondary">
              Explore 19 Tools <ArrowRight className="h-4 w-4 inline ml-1" />
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-5 mt-8 text-sm text-[#a0a8b8] animate-fade-in stagger-3">
            <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#10b981]" /> 100% Free Forever</span>
            <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#10b981]" /> No Credit Card Required</span>
            <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#10b981]" /> Instant Access to All Tools</span>
          </div>
        </div>
      </section>

      {/* S2: Tool Categories Showcase */}
      <section className="py-20 lg:py-28 px-4" data-testid="categories-section">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-[40px] font-bold text-white" style={{ letterSpacing: '-0.02em' }}>3 Categories. 19 Tools. Unlimited Insights.</h2>
            <p className="text-[#a0a8b8] text-base lg:text-lg mt-4">Everything you need to play smarter, compete with friends, and stay in control</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { Icon: Target, title: 'Play Smart Tools', count: '7 calculators & analyzers', countColor: '#4d7cfe', borderColor: '#4d7cfe', desc: 'Make data-driven decisions with budget calculators, RTP comparison, bankroll simulators, and bonus decoders.', audience: 'Strategic players who value math over luck', cta: 'View 7 Tools', filter: 'play-smart' },
              { Icon: Rocket, title: 'Viral Tools', count: '6 interactive experiences', countColor: '#8b5cf6', borderColor: '#8b5cf6', desc: 'Discover your gambler personality, predict outcomes, challenge friends, and track your betting streaks.', audience: 'Social players who love competition', cta: 'View 6 Tools', filter: 'viral' },
              { Icon: Heart, title: 'Emotional Wellness', count: '6 responsible gaming tools', countColor: '#10b981', borderColor: '#10b981', desc: 'Mood tracking, loss-chase detection, session timers, and healthy alternatives for mindful gambling.', audience: 'Players who want to stay in control', cta: 'View 6 Tools', filter: 'wellness' },
            ].map((cat, i) => (
              <Link key={i} to={`/tools?category=${cat.filter}`}
                className="group bg-[#20263a] rounded-2xl p-10 border border-white/10 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 relative"
                style={{ borderBottom: `4px solid ${cat.borderColor}` }}
                data-testid={`category-card-${i}`}>
                <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-5" style={{ background: `${cat.borderColor}15` }}>
                  <cat.Icon className="h-8 w-8" style={{ color: cat.borderColor }} />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-1">{cat.title}</h3>
                <p className="text-sm font-medium mb-3" style={{ color: cat.countColor }}>{cat.count}</p>
                <p className="text-[#a0a8b8] text-base leading-relaxed mb-4">{cat.desc}</p>
                <p className="text-sm text-[#a0a8b8]"><strong className="text-white">Perfect for:</strong> {cat.audience}</p>
                <div className="mt-6 w-full py-3 rounded-lg border border-white/15 text-white text-sm font-medium text-center group-hover:bg-[#4d7cfe] group-hover:border-[#4d7cfe] transition-all">
                  {cat.cta} <ArrowRight className="h-3.5 w-3.5 inline ml-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* S3: Featured Tools Grid */}
      <section className="py-20 px-4 bg-[#181d2a]" data-testid="featured-tools-section">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-[40px] font-bold text-white" style={{ letterSpacing: '-0.02em' }}>Most Popular Tools This Week</h2>
            <p className="text-[#a0a8b8] text-base lg:text-lg mt-4">The tools our community uses most to play smarter</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURED_TOOLS.map((tool, i) => (
              <Link key={i} to={tool.path} className="group bg-[#20263a] rounded-xl p-8 border border-white/10 hover:-translate-y-0.5 hover:border-[#4d7cfe]/30 transition-all relative min-h-[280px] flex flex-col" data-testid={`featured-tool-${i}`}>
                {tool.badge && <span className="absolute top-5 right-5 text-[11px] font-semibold uppercase px-3 py-1 rounded-full text-white" style={{ background: tool.badgeColor }}>{tool.badge}</span>}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${tool.iconColor}15` }}>
                  <tool.Icon className="h-6 w-6" style={{ color: tool.iconColor }} />
                </div>
                <h3 className="text-xl font-semibold text-white group-hover:text-[#4d7cfe] transition-colors">{tool.name}</h3>
                <p className="text-sm text-[#a0a8b8] mt-2 leading-relaxed">{tool.desc}</p>
                <p className="text-xs text-[#8b5cf6] mt-4">{tool.stat}</p>
                <div className="mt-auto pt-5 w-full py-3 rounded-lg border border-white/15 text-white text-sm font-medium text-center group-hover:bg-[#4d7cfe] group-hover:border-[#4d7cfe] transition-all">
                  {tool.cta} <ArrowRight className="h-3.5 w-3.5 inline ml-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* S4: How It Works */}
      <section className="py-20 lg:py-28 px-4" data-testid="how-it-works-section">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-[40px] font-bold text-white" style={{ letterSpacing: '-0.02em' }}>Get Started in 30 Seconds</h2>
            <p className="text-[#a0a8b8] text-base lg:text-lg mt-4">No credit card. No commitments. Just smart tools.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {/* Connectors (desktop) */}
            <div className="hidden md:flex absolute top-1/2 left-[33.3%] -translate-x-1/2 -translate-y-1/2 text-white/20 z-10"><ChevronRight className="h-8 w-8" /></div>
            <div className="hidden md:flex absolute top-1/2 left-[66.6%] -translate-x-1/2 -translate-y-1/2 text-white/20 z-10"><ChevronRight className="h-8 w-8" /></div>
            {[
              { num: '01', Icon: Mail, iconColor: '#4d7cfe', title: 'Sign Up Free', desc: 'Create your account in under 30 seconds', features: ['No credit card required', '100% free forever', 'Instant access'] },
              { num: '02', Icon: Target, iconColor: '#8b5cf6', title: 'Choose Your Tool', desc: 'Pick from 19 tools across 3 categories', features: ['All tools included', 'No paywalls', 'Use as many as you want'] },
              { num: '03', Icon: BarChart3, iconColor: '#10b981', title: 'Get Smart Insights', desc: 'Instantly access calculations & advice', features: ['Real-time results', 'Save & track over time', 'Share with friends'] },
            ].map((step, i) => (
              <div key={i} className="bg-[#20263a] rounded-xl p-10 border border-white/10 relative" data-testid={`step-${i}`}>
                <span className="absolute top-4 right-6 text-5xl font-bold text-white/[0.04]">{step.num}</span>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ background: `${step.iconColor}15` }}>
                  <step.Icon className="h-7 w-7" style={{ color: step.iconColor }} />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-[#a0a8b8] text-base mb-6">{step.desc}</p>
                <ul className="space-y-2">
                  {step.features.map((f, fi) => (
                    <li key={fi} className="text-sm text-[#a0a8b8] flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#10b981] shrink-0" /> {f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link to="/signup" className="inline-block px-12 py-4 rounded-lg bg-[#4d7cfe] text-white text-lg font-semibold hover:brightness-110 hover:-translate-y-0.5 transition-all shadow-lg shadow-[#4d7cfe]/20" data-testid="hiw-cta">
              Get Started Now <ArrowRight className="h-4 w-4 inline ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* S6: All Tools Preview */}
      <section className="py-20 lg:py-28 px-4" data-testid="all-tools-section">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-[40px] font-bold text-white" style={{ letterSpacing: '-0.02em' }}>Explore All 19 Tools</h2>
            <p className="text-[#a0a8b8] text-base lg:text-lg mt-4">Every tool is free, forever. No hidden paywalls.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Object.entries(ALL_TOOLS).map(([catName, cat]) => (
              <div key={catName} className="bg-[#20263a] rounded-xl p-8 border border-white/10">
                <h3 className="text-lg font-semibold mb-5" style={{ color: cat.color }}>{catName} ({cat.tools.length})</h3>
                <ul className="space-y-1">
                  {cat.tools.map((tool, i) => (
                    <li key={i}><Link to={tool.path} className="text-[#a0a8b8] text-[15px] leading-[2.2] hover:text-white hover:pl-1 transition-all inline-flex items-center gap-1 group">{tool.name} <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" /></Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/tools" className="inline-block px-8 py-3.5 rounded-lg border border-white/20 text-white text-base font-medium hover:border-[#4d7cfe] transition-colors" data-testid="view-library-btn">
              View Full Tool Library <ArrowRight className="h-4 w-4 inline ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* S7: Final CTA */}
      <section className="py-20 lg:py-28 px-4" data-testid="cta-section">
        <div className="max-w-[900px] mx-auto">
          <div className="bg-gradient-to-br from-[#2a3a5c] to-[#1a1f2e] rounded-2xl p-12 lg:p-20 border border-[#4d7cfe]/30 shadow-lg shadow-[#4d7cfe]/10 text-center">
            <h2 className="text-3xl lg:text-[40px] font-bold text-white" style={{ letterSpacing: '-0.02em' }}>Ready to Play Smarter?</h2>
            <p className="text-xl text-[#a0a8b8] mt-4">Start making data-driven decisions today</p>
            <form method="post" onSubmit={handleGetAccess} className="flex flex-col sm:flex-row gap-3 max-w-[600px] mx-auto mt-12">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" required
                className="flex-1 h-[52px] px-5 rounded-lg bg-white/[0.08] border border-white/15 text-white text-base placeholder:text-white/40 focus:outline-none focus:border-[#4d7cfe]"
                data-testid="cta-email-input" />
              <button type="submit" className="h-[52px] px-8 rounded-lg bg-[#4d7cfe] text-white text-base font-semibold whitespace-nowrap hover:brightness-110 hover:scale-[1.02] transition-all" data-testid="cta-submit-btn">
                Get Free Access
              </button>
            </form>
            <p className="text-xs text-white/50 mt-6 leading-relaxed max-w-lg mx-auto">
              By signing up, you agree to our <Link to="/terms" className="text-[#4d7cfe] hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-[#4d7cfe] hover:underline">Privacy Policy</Link>, and consent to receive promotional emails from CasinoIQ and our casino partners. Unsubscribe anytime. 18+ only.
            </p>
            <div className="flex flex-wrap justify-center gap-10 mt-10">
              <span className="flex items-center gap-2 text-sm text-[#a0a8b8]"><Shield className="h-4 w-4" /> Secure Signup</span>
              <span className="flex items-center gap-2 text-sm text-[#a0a8b8]"><Shield className="h-4 w-4" /> Email Privacy</span>
              <span className="flex items-center gap-2 text-sm text-[#a0a8b8]"><Shield className="h-4 w-4" /> Responsible Gambling</span>
            </div>
          </div>
        </div>
      </section>

      {/* S8: Footer */}
      <footer className="bg-[#0f1419] pt-20 pb-10 px-4" data-testid="main-footer">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-widest mb-5">Tools</h4>
              <ul className="space-y-2">
                <li><Link to="/tools" className="text-sm text-[#a0a8b8] hover:text-white hover:pl-1 transition-all">All Tools</Link></li>
                <li><Link to="/tools?category=play-smart" className="text-sm text-[#a0a8b8] hover:text-white hover:pl-1 transition-all">Play Smart</Link></li>
                <li><Link to="/tools?category=viral" className="text-sm text-[#a0a8b8] hover:text-white hover:pl-1 transition-all">Viral Tools</Link></li>
                <li><Link to="/tools?category=wellness" className="text-sm text-[#a0a8b8] hover:text-white hover:pl-1 transition-all">Emotional Wellness</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-widest mb-5">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/casino-partners" className="text-sm text-[#a0a8b8] hover:text-white hover:pl-1 transition-all">Casino Partners</Link></li>
                <li><span className="text-sm text-[#a0a8b8]">How It Works</span></li>
                <li><span className="text-sm text-[#a0a8b8]">About CasinoIQ</span></li>
                <li><span className="text-sm text-[#a0a8b8]">Contact Us</span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-widest mb-5">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-sm text-[#a0a8b8] hover:text-white hover:pl-1 transition-all" data-testid="footer-terms-link">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-sm text-[#a0a8b8] hover:text-white hover:pl-1 transition-all" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><span className="text-sm text-[#a0a8b8]">Cookie Policy</span></li>
                <li><a href="https://www.ncpgambling.org/" target="_blank" rel="noopener noreferrer" className="text-sm text-[#a0a8b8] hover:text-white transition-colors">Responsible Gambling</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-widest mb-5">Casino Partners</h4>
              <ul className="space-y-2">
                <li><a href="https://bzstarz1.com/bhi8kvogt?utm_source=casinoiq" target="_blank" rel="noopener noreferrer" className="text-sm text-[#a0a8b8] hover:text-white transition-colors flex items-center gap-1">BitStarz Casino <ExternalLink className="h-3 w-3" /></a></li>
                <li><Link to="/casino-partners" className="text-sm text-[#a0a8b8] hover:text-white transition-colors">View All Partners</Link></li>
              </ul>
            </div>
          </div>
          <div className="h-px bg-white/10 mb-8" />
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="text-center lg:text-left">
              <p className="text-sm text-[#a0a8b8]">&copy; 2026 CasinoIQ. All rights reserved.</p>
              <p className="text-xs text-white/40 mt-2 max-w-[700px] leading-relaxed">
                CasinoIQ promotes responsible gambling. If you or someone you know has a gambling problem, contact:&nbsp;
                <a href="https://www.ncpgambling.org/" target="_blank" rel="noopener noreferrer" className="text-[#4d7cfe] hover:underline">National Gambling Helpline</a> |&nbsp;
                <a href="https://www.begambleaware.org/" target="_blank" rel="noopener noreferrer" className="text-[#4d7cfe] hover:underline">BeGambleAware.org</a>
              </p>
            </div>
            <div className="flex items-center gap-5">
              <a href="#" className="text-[#a0a8b8] hover:text-[#4d7cfe] hover:scale-110 transition-all"><Twitter className="h-5 w-5" /></a>
              <a href="#" className="text-[#a0a8b8] hover:text-[#4d7cfe] hover:scale-110 transition-all"><Instagram className="h-5 w-5" /></a>
              <a href="#" className="text-[#a0a8b8] hover:text-[#4d7cfe] hover:scale-110 transition-all"><MessageCircle className="h-5 w-5" /></a>
              <a href="#" className="text-[#a0a8b8] hover:text-[#4d7cfe] hover:scale-110 transition-all"><Send className="h-5 w-5" /></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
